/*
 * The gift card checkout webview injects window.apAppGiftCardAmount as an
 * { amount, currency } object, while the in-app browser flow injects the legacy
 * amount string. Both flows read the plain amount value through this helper.
 */
export const getInjectedGiftCardAmount = (
    value: { amount: string; currency: string } | string | null | undefined
): string => {
    if (value === null || value === undefined) {
        return "";
    }
    if (typeof value === "string") {
        return value;
    }
    return value.amount ?? "";
};

/*
 * Port of the mobile wallet's getMerchantName (app/loyalty/loyaltyHelper.ts):
 * gift card names can carry a "**promo**" annotation after the merchant name.
 */
export const getGiftCardDisplayName = (name: string | null | undefined): string => {
    if (!name) {
        return "";
    }
    return name.includes("**") ? name.split("**")[0].trim() : name;
};

const GIFT_CARD_PROVIDER_DISPLAY_NAMES: Record<string, string> = {
    BLACKHAWK: "Blackhawk",
    PREZZEE: "Prezzee",
};

export const getGiftCardProviderDisplayName = (
    supplier: string | null | undefined
): string | undefined =>
    supplier ? GIFT_CARD_PROVIDER_DISPLAY_NAMES[supplier.toUpperCase()] : undefined;

const CURRENCY_SYMBOL_BY_CURRENCY_CODE: Record<string, string> = {
    AUD: "$",
    USD: "$",
    NZD: "$",
    CAD: "$",
    GBP: "£",
};

/*
 * Port of the mobile wallet's formatMoney (app/helpers/currencyHelper.ts),
 * extended to accept the legacy string amount injected by the in-app browser.
 */
export const formatGiftCardMoney = (
    value: { amount: string; currency: string } | string | null | undefined,
    locale: string,
    short = false
): string => {
    const amount = getInjectedGiftCardAmount(value);
    const currency = typeof value === "object" && value !== null ? value.currency : "";
    const symbol = CURRENCY_SYMBOL_BY_CURRENCY_CODE[currency] ?? "";
    const number = amount === "" ? NaN : Number(amount);

    if (!isFinite(number) || !currency) {
        return `${symbol}-`;
    }

    return Intl.NumberFormat(locale, {
        style: "currency",
        currency,
        minimumFractionDigits: short ? 0 : 2,
    }).format(number);
};
